import { useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { useChatAssistantAttachments } from "@/contexts/useChatAssistantAttachments";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import { Box, Button, CircularProgress, Grid, Menu, MenuItem, Stack, useMediaQuery, useTheme } from "@mui/material";
import {
  Aggregate,
  ColumnDirective,
  ColumnsDirective,
  ExcelExport,
  Filter,
  GridComponent,
  Group,
  Inject,
  Page,
  PdfExport,
  Sort,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import DataFormSelect from "components/Core/DataFormSelect";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import DataGridContext from "core/DataGridContext";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { ChatAssistantButton } from "mui-toolkit/ChatAssistantButton";
import { useSnackbar } from "notistack";

import { UsersApiHelper } from "../../../api/ApiHelper";
import { ReportsIcon } from "../../Icons/Icons";
import ResponsiveReportCardGrid from "../../shared/DataGrid/ResponsiveReportCardGrid";
import { fsaSort } from "./helpers";

export default function BaseAcresReport() {
  const defaultPageSize = 20;
  const pageSizes = [5, 10, 15, 20, 50, 100, 200, 1000];
  const gridKey = "BaseAcresReport";

  const { globalState, globalDispatch } = useContext(AppContext);
  const { getGridState, setGridState } = useContext(DataGridContext);
  const { addAttachments } = useChatAssistantAttachments();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  const gridRef = useRef(null);

  const [currentYear, setCurrentYear] = useState(
    globalState?.crop_year ? globalState?.crop_year : getCurrentReinsuranceYear()
  );
  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: [],
  });
  const [baseAcres, setBaseAcres] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 1, pageSize: 10 });

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  const loadBaseAcres = async () => {
    try {
      const baseAcresFilter = {
        producer_token: globalState.producer_token,
        crop_year: currentYear,
        is_active: true,
      };
      const baseAcres = await UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState);
      setBaseAcres(baseAcres);
    } catch (err) {
      setBaseAcres([]);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear, globalDispatch]);

  useEffect(() => {
    if (currentYear && globalState?.producer_token) {
      loadBaseAcres();
    }
  }, [currentYear, globalState?.producer_token]);

  useEffect(() => {
    if (baseAcres?.length) {
      addAttachments([
        {
          name: "base_acres",
          content: baseAcres,
        },
      ]);
    }
  }, [baseAcres, addAttachments]);

  const filteredBaseAcres = useMemo(() => {
    if (!baseAcres?.length) return [];

    if (!filter?.commodity_name?.length) {
      return baseAcres;
    }

    return baseAcres.filter((row) => filter.commodity_name.includes(row.commodity_name));
  }, [baseAcres, filter.commodity_name]);

  const pageOptionsSummary = {
    pageSize: getGridState()[gridKey]?.pageSize || defaultPageSize,
    pageSizes: pageSizes,
  };

  const columnsDirective = useMemo(
    () => [
      { field: "crop_year", headerText: "Year", width: 80, textAlign: "Right" },
      { field: "location_county_name", headerText: "County", width: 100 },
      { field: "location_state_name", headerText: "State", width: 100 },
      { field: "fsa_farm_number", headerText: "Farm Number", width: 120, textAlign: "Right", sortComparer: fsaSort },
      { field: "farm_name", headerText: "Farm Name", width: 150 },
      { field: "commodity_name", headerText: "Commodity", width: 120 },
      { field: "type_name", headerText: "Type", width: 100 },
      { field: "base_acreage", headerText: "Base Acres", width: 120, textAlign: "Right", format: "N2" },
      { field: "plc_yield", headerText: "PLC Yield", width: 120, textAlign: "Right", format: "N2" },
    ],
    []
  );

  const filterHandleChangeState = (event, value) => {
    value = value ?? event.target.value;

    dispatchFilter({
      type: "update",
      payload: { key: event.target.name, value: value },
    });
  };

  const handleActionComplete = (args) => {
    if (args.requestType === "sorting" || args.requestType === "paging") {
      if (args.requestType === "sorting") {
        setGridState(
          gridKey,
          args.columnName && args.direction ? { sorting: { field: args.columnName, sort: args.direction } } : null
        );
      }

      if (args.requestType === "paging") {
        setGridState(gridKey, { pageSize: args.pageSize });
      }
    }
  };

  const handleGridToolbarClick = async (args) => {
    const grid = gridRef.current;
    if (!grid) return;
    if (args?.item?.id?.toLowerCase().includes("excel")) {
      grid.excelExport();
    } else if (args?.item?.id?.toLowerCase().includes("pdf")) {
      grid.pdfExport();
    }
  };

  return (
    <>
      <WorkspaceHeader
        title="Base Acres"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />

      <Grid container className="navigation" spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6} lg={4}>
          <DataFormSelect
            value={filter.commodity_name || ""}
            name="commodity_name"
            isRequired={false}
            onChange={filterHandleChangeState}
            objectName="Commodity"
            itemFilter={{ reinsurance_year: String(currentYear) }}
            itemGetKey={(item) => item.commodity_name}
            itemGetLabel={(item) => item.commodity_name}
            doFetch={currentYear !== null}
            multiple={true}
            limitTags={isSmall ? 2 : 3}
            autoSelect={false}
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ mb: 2 }}
        className="no-print"
      >
        <Box className="report_viewer" sx={{ display: "flex", justifyContent: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <PopupState variant="popover" popupId="demo-popup-menu">
            {(popupState) => (
              <>
                <Button variant="outlined" {...bindTrigger(popupState)} className="year-filter">
                  {currentYear} <ArrowDropDownOutlined />
                </Button>
                <Menu {...bindMenu(popupState)}>
                  {getBudgetYears()?.map((year, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setCurrentYear(year);
                        popupState.close();
                      }}
                    >
                      {year}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </PopupState>

          <ChatAssistantButton systemPrompt="tilley_my_farm_data" placeholder="Ask Tilley..." />
        </Box>
      </Stack>

      {isLoading && <CircularProgress />}

      {!isLoading && filteredBaseAcres?.length > 0 && (
        <>
          <GridComponent
            cssClass={"tilley tilley-grid-summary"}
            style={{ color: theme.palette.primary.light }}
            dataSource={filteredBaseAcres}
            allowSorting={true}
            allowGrouping={false}
            allowPaging={true}
            pageSettings={pageOptionsSummary}
            sortSettings={
              getGridState()[gridKey]?.sorting
                ? {
                    columns: [
                      {
                        field: getGridState()[gridKey]?.sorting.field,
                        direction: getGridState()[gridKey]?.sorting.sort,
                      },
                    ],
                  }
                : { columns: [] }
            }
            toolbar={["ExcelExport", "PdfExport"]}
            toolbarClick={handleGridToolbarClick}
            allowPdfExport={true}
            allowExcelExport={true}
            groupSettings={{
              showDropArea: false,
              columns: [],
            }}
            ref={gridRef}
            actionComplete={handleActionComplete}
          >
            <ColumnsDirective>
              {columnsDirective?.map((item, index) => (
                <ColumnDirective key={index} {...item} />
              ))}
            </ColumnsDirective>
            <Inject services={[Sort, Filter, Group, Aggregate, Page, Toolbar, ExcelExport, PdfExport]} />
          </GridComponent>
          {isSmall && (
            <ResponsiveReportCardGrid
              columns={columnsDirective}
              rows={filteredBaseAcres}
              loading={isLoading}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          )}
        </>
      )}

      {!isLoading && !filteredBaseAcres?.length && (
        <EmptyCard
          text="No base acres found"
          secondaryText="Remove some filters or add a new base acres in the Farm Vault"
        />
      )}
    </>
  );
}
